import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faMobile, faDownload, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import authService from '../api/authService';
import '../styles/MobileAuthModal.css';

// Common country codes
const COUNTRY_CODES = [
  { code: '1', country: 'US', name: 'United States', flag: '🇺🇸' },
  { code: '1', country: 'CA', name: 'Canada', flag: '🇨🇦' },
  { code: '44', country: 'GB', name: 'United Kingdom', flag: '🇬🇧' },
  { code: '33', country: 'FR', name: 'France', flag: '🇫🇷' },
  { code: '49', country: 'DE', name: 'Germany', flag: '🇩🇪' },
  { code: '39', country: 'IT', name: 'Italy', flag: '🇮🇹' },
  { code: '34', country: 'ES', name: 'Spain', flag: '🇪🇸' },
  { code: '31', country: 'NL', name: 'Netherlands', flag: '🇳🇱' },
  { code: '46', country: 'SE', name: 'Sweden', flag: '🇸🇪' },
  { code: '47', country: 'NO', name: 'Norway', flag: '🇳🇴' },
  { code: '45', country: 'DK', name: 'Denmark', flag: '🇩🇰' },
  { code: '41', country: 'CH', name: 'Switzerland', flag: '🇨🇭' },
  { code: '43', country: 'AT', name: 'Austria', flag: '🇦🇹' },
  { code: '32', country: 'BE', name: 'Belgium', flag: '🇧🇪' },
  { code: '351', country: 'PT', name: 'Portugal', flag: '🇵🇹' },
  { code: '353', country: 'IE', name: 'Ireland', flag: '🇮🇪' },
  { code: '358', country: 'FI', name: 'Finland', flag: '🇫🇮' },
  { code: '420', country: 'CZ', name: 'Czech Republic', flag: '🇨🇿' },
  { code: '48', country: 'PL', name: 'Poland', flag: '🇵🇱' },
  { code: '36', country: 'HU', name: 'Hungary', flag: '🇭🇺' },
  { code: '972', country: 'IL', name: 'Israel', flag: '🇮🇱' },
];

const MobileAuthModal = ({ isOpen, onClose, onSuccess, onSuccessfulLogin, title, subtitle }) => {
  const [step, setStep] = useState('phone'); // 'phone', 'verification', 'notRegistered'
  const [selectedCountry, setSelectedCountry] = useState(COUNTRY_CODES[0]); // Default to US
  const [phoneNumber, setPhoneNumber] = useState('');
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [code, setCode] = useState('');
  const [userId, setUserId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Format phone number as user types (remove non-digits)
  const handlePhoneNumberChange = (e) => {
    const value = e.target.value.replace(/\D/g, ''); // Remove non-digits
    setPhoneNumber(value);
  };

  // Get full phone number for API calls
  const getFullPhoneNumber = () => {
    return `+${selectedCountry.code}${phoneNumber}`;
  };

  // Get display phone number (with + for display)
  const getDisplayPhoneNumber = () => {
    return `+${selectedCountry.code}${phoneNumber}`;
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!phoneNumber || phoneNumber.length < 6) {
      setError('Please enter a valid phone number');
      return;
    }
    
    setLoading(true);

    try {
      const fullPhoneNumber = getDisplayPhoneNumber();
      console.log('Submitting phone number:', fullPhoneNumber);
      
      // Use authService for consistent API handling
      const data = await authService.mobileLoginRequest(fullPhoneNumber);
      console.log('Server response:', data);

      if (data.success) {
        setUserId(data.userId);
        setStep('verification');
      } else if (data.requiresApp) {
        setStep('notRegistered');
      } else {
        // Check if it's the "no registered account" message and customize it
        if (data.message && data.message.includes('No registered account found with this phone number')) {
          setError(
            <span>
              No registered account found with this phone number. Register on the Yesod app{' '}
              <a 
                href="https://apps.apple.com/us/app/yesod/id6748472269" 
                target="_blank" 
                rel="noopener noreferrer"
                style={{ color: '#007AFF', textDecoration: 'underline' }}
              >
                here
              </a>
            </span>
          );
        } else {
          setError(data.message || 'Failed to send verification code');
        }
      }
    } catch (err) {
      console.error('Network error:', err);
      const errorMessage = err.response?.data?.message || 'Network error. Please try again.';
      
      // Check if it's the "no registered account" message and customize it
      if (errorMessage.includes('No registered account found with this phone number')) {
        setError(
          <span>
            No registered account found with this phone number. Register on the Yesod app{' '}
            <a 
              href="https://apps.apple.com/us/app/yesod/id6748472269" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{ color: '#007AFF', textDecoration: 'underline' }}
            >
              here
            </a>
          </span>
        );
      } else {
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('📱 Verifying mobile login code...');
      console.log('📱 Mobile login - Current URL before verification:', window.location.href);
      
      const fullPhoneNumber = getDisplayPhoneNumber();
      
      // Use authService for proper token handling
      const data = await authService.mobileLoginVerify(fullPhoneNumber, code, userId);

      if (data.success) {
        console.log('📱 Mobile login successful via authService');
        console.log('📱 Mobile login - Current URL after auth storage:', window.location.href);
        
        // Call both callbacks for backward compatibility
        if (onSuccessfulLogin) {
          onSuccessfulLogin(data.user);
        }
        if (onSuccess) {
          onSuccess(data.user);
        }
        
        console.log('📱 Mobile login - Callbacks complete, closing modal');
        console.log('📱 Mobile login - Current URL after callbacks:', window.location.href);
        
        onClose();
        
        console.log('📱 Mobile login - Modal closed');
        console.log('📱 Mobile login - Final URL:', window.location.href);
      } else {
        setError(data.message || 'Invalid verification code');
      }
    } catch (err) {
      console.error('Mobile login verification error:', err);
      setError(err.response?.data?.message || 'Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetModal = () => {
    setStep('phone');
    setSelectedCountry(COUNTRY_CODES[0]);
    setPhoneNumber('');
    setCode('');
    setUserId('');
    setError('');
    setLoading(false);
    setShowCountryDropdown(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="mobile-auth-overlay">
      <div className="mobile-auth-modal">
        <div className="modal-header">
          <h2 className="modal-title">
            {title || (step === 'phone' && 'Phone number') || 
             (step === 'verification' && 'Enter code') || 
             (step === 'notRegistered' && 'Join Yesod')}
          </h2>
          {subtitle && <p className="modal-subtitle">{subtitle}</p>}
          <button className="close-button" onClick={handleClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-content">
          {step === 'phone' && (
            <form onSubmit={handlePhoneSubmit}>
              <div className="form-field">
                <label className="form-label">We'll text you a code</label>
                <div className="phone-input-container">
                  <div className="country-selector" onClick={() => setShowCountryDropdown(!showCountryDropdown)}>
                    <span className="country-flag">{selectedCountry.flag}</span>
                    <span className="country-code">+{selectedCountry.code}</span>
                    <FontAwesomeIcon icon={faChevronDown} className={`dropdown-arrow ${showCountryDropdown ? 'open' : ''}`} />
                  </div>
                  
                  {showCountryDropdown && (
                    <div className="country-dropdown">
                      {COUNTRY_CODES.map((country, index) => (
                        <div
                          key={`${country.code}-${country.country}-${index}`}
                          className="country-option"
                          onClick={() => {
                            setSelectedCountry(country);
                            setShowCountryDropdown(false);
                          }}
                        >
                          <span className="country-flag">{country.flag}</span>
                          <span className="country-name">{country.name}</span>
                          <span className="country-code">+{country.code}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <input
                    type="tel"
                    className="phone-number-input"
                    placeholder="Phone number"
                    value={phoneNumber}
                    onChange={handlePhoneNumberChange}
                    required
                  />
                </div>
              </div>
              
              {error && <div className="error-message">{error}</div>}
              
              <button 
                type="submit" 
                className="auth-button"
                disabled={loading || !phoneNumber}
              >
                {loading ? 'Sending...' : 'Continue'}
              </button>
            </form>
          )}

          {step === 'verification' && (
            <form onSubmit={handleVerificationSubmit}>
              <div className="verification-info">
                <p>We sent a code to {getDisplayPhoneNumber()}</p>
              </div>
              
              <div className="form-field">
                <input
                  type="text"
                  className="form-input code-input"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  maxLength="6"
                  required
                />
              </div>
              
              {error && <div className="error-message">{error}</div>}
              
              <button 
                type="submit" 
                className="auth-button"
                disabled={loading || code.length !== 6}
              >
                {loading ? 'Verifying...' : 'Continue'}
              </button>
            </form>
          )}

          {step === 'notRegistered' && (
            <div className="not-registered-content">
              <div className="not-registered-icon">
                <FontAwesomeIcon icon={faMobile} />
              </div>
              <h3>Get the app first</h3>
              <p>
                To sign in with your phone, download the Yesod app and create your account there first.
              </p>
              
              <a 
                href="https://apps.apple.com/app/yesod/id6475827278" 
                target="_blank" 
                rel="noopener noreferrer"
                className="app-store-button"
              >
                <FontAwesomeIcon icon={faDownload} />
                Download Yesod
              </a>
              
              <button 
                type="button" 
                className="back-button"
                onClick={() => setStep('phone')}
              >
                Try a different number
              </button>
              
              <div className="help-text">
                Already have the app? Make sure your phone number is verified in Settings.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileAuthModal;
